var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { UI } from './theme';
import { getParts, getState } from './helpers';
import { resolveEffectiveUiTokens } from './nodeTheme';
import { createThemeScope } from '../theme/themeScope';
import { parseCssPx, resolveFontSizeTriple } from '../theme/themeUtils';
import { colorWithAlpha } from '../utils/color';
function cloneShadow(s) {
    return { color: s.color, blur: s.blur, offsetX: s.offsetX, offsetY: s.offsetY };
}
/** Merge app UI tokens onto canvas defaults. Empty tokens → identical to `UI`. */
export function resolveUiCanvasTheme(tokens) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
    if (tokens === void 0) { tokens = {}; }
    var primary = (_a = tokens.primary) !== null && _a !== void 0 ? _a : UI.primary;
    return __assign(__assign({ primary: primary, primaryHover: (_b = tokens.primaryHover) !== null && _b !== void 0 ? _b : UI.primaryHover, primaryActive: (_c = tokens.primaryActive) !== null && _c !== void 0 ? _c : UI.primaryActive, primarySubtle: (_d = tokens.primarySubtle) !== null && _d !== void 0 ? _d : UI.primarySubtle, primaryMuted: (_e = tokens.primarySubtle) !== null && _e !== void 0 ? _e : UI.primaryMuted, secondary: (_f = tokens.secondary) !== null && _f !== void 0 ? _f : UI.secondary, secondaryHover: (_g = tokens.secondaryHover) !== null && _g !== void 0 ? _g : UI.secondaryHover, success: (_h = tokens.success) !== null && _h !== void 0 ? _h : UI.success, successBg: (_j = tokens.successSubtle) !== null && _j !== void 0 ? _j : UI.successBg, warning: (_k = tokens.warning) !== null && _k !== void 0 ? _k : UI.warning, danger: (_l = tokens.danger) !== null && _l !== void 0 ? _l : UI.danger, surface: (_m = tokens.surface) !== null && _m !== void 0 ? _m : UI.surface, surfaceMuted: (_o = tokens.surfaceMuted) !== null && _o !== void 0 ? _o : UI.surfaceMuted, surfaceInset: (_p = tokens.surfaceInset) !== null && _p !== void 0 ? _p : UI.surfaceInset, overlay: (_q = tokens.overlay) !== null && _q !== void 0 ? _q : UI.overlay, border: (_r = tokens.border) !== null && _r !== void 0 ? _r : UI.border, borderStrong: (_s = tokens.borderStrong) !== null && _s !== void 0 ? _s : UI.borderStrong, borderFocus: (_t = tokens.primary) !== null && _t !== void 0 ? _t : UI.borderFocus, text: (_u = tokens.text) !== null && _u !== void 0 ? _u : UI.text, textSecondary: (_v = tokens.textSecondary) !== null && _v !== void 0 ? _v : UI.textSecondary, textMuted: (_w = tokens.textMuted) !== null && _w !== void 0 ? _w : UI.textMuted, textInverse: (_x = tokens.textInverse) !== null && _x !== void 0 ? _x : UI.textInverse, textPlaceholder: (_y = tokens.placeholder) !== null && _y !== void 0 ? _y : UI.textPlaceholder, radius: parseCssPx(tokens.radius, UI.radius), radiusSm: parseCssPx(tokens.radiusSm, UI.radiusSm), radiusLg: parseCssPx(tokens.radiusLg, UI.radiusLg), radiusFull: UI.radiusFull, font: (_z = tokens.fontFamily) !== null && _z !== void 0 ? _z : UI.font }, resolveFontSizeTriple(tokens, {
        fontSize: UI.fontSize,
        fontSizeSm: UI.fontSizeSm,
        fontSizeLg: UI.fontSizeLg,
    })), { controlHeight: parseCssPx(tokens.controlHeight, UI.controlHeight), inputHeight: parseCssPx(tokens.controlHeight, UI.inputHeight), spaceXs: parseCssPx(tokens.spaceXs, UI.spaceXs), spaceSm: parseCssPx(tokens.spaceSm, UI.spaceSm), spaceMd: parseCssPx(tokens.spaceMd, UI.spaceMd), spaceLg: parseCssPx(tokens.spaceLg, UI.spaceLg), spaceXl: parseCssPx(tokens.spaceXl, UI.spaceXl), shadowSm: cloneShadow(UI.shadowSm), shadowMd: tokens.shadowMd
            ? { color: tokens.shadowMd, blur: UI.shadowMd.blur, offsetX: 0, offsetY: 2 }
            : cloneShadow(UI.shadowMd), shadowLg: cloneShadow(UI.shadowLg), shadowPrimary: {
            color: (_0 = colorWithAlpha(primary, 0.28)) !== null && _0 !== void 0 ? _0 : UI.shadowPrimary.color,
            blur: UI.shadowPrimary.blur,
            offsetX: UI.shadowPrimary.offsetX,
            offsetY: UI.shadowPrimary.offsetY,
        } });
}
var canvasUiScope = createThemeScope(function () { return resolveUiCanvasTheme({}); });
/** Sync App-scoped canvas palette (called from App on init / setUiTheme / create). */
export function syncActiveCanvasUiTheme(tokens, app) {
    return canvasUiScope.sync(resolveUiCanvasTheme(tokens), app !== null && app !== void 0 ? app : undefined);
}
/**
 * Current canvas UI palette for builders.
 * Prefer calling inside a create/refresh (stack); optional `app` uses WeakMap snapshot.
 */
export function getActiveUi(app) {
    return canvasUiScope.getActive(app !== null && app !== void 0 ? app : undefined);
}
/** Run factory/refresh with a fixed palette on the build stack. */
export function runWithCanvasUiTheme(theme, fn) {
    return canvasUiScope.runWithResult(theme, fn);
}
/** Resolve palette for an app instance (does not mutate the active theme). */
export function getCanvasUiTheme(app) {
    if (app && typeof app.getResolvedTheme === 'function') {
        return resolveUiCanvasTheme(app.getResolvedTheme());
    }
    return resolveUiCanvasTheme({});
}
var UI_COMPONENT_TYPES = new Set([
    'button',
    'card',
    'progressBar',
    'slider',
    'checkbox',
    'toggle',
    'input',
    'textarea',
    'radio',
    'tooltip',
    'menu',
    'dialog',
    'tabs',
    'accordion',
    'table',
    'tree',
    'toolbar',
    'toast',
    'statusBar',
    'label',
]);
function setFill(node, fill) {
    if (!node)
        return;
    node.fill = fill;
}
function setStroke(node, stroke) {
    if (!node)
        return;
    node.stroke = stroke;
}
function setFontSize(node, size) {
    if (!node || !Number.isFinite(size))
        return;
    node.fontSize = size;
}
function fontSizeForControl(ui, size) {
    if (size === 'sm')
        return ui.fontSizeSm;
    if (size === 'lg')
        return ui.fontSizeLg;
    return ui.fontSize;
}
function buttonFill(ui, variant, customFill, disabled) {
    if (disabled)
        return ui.borderStrong;
    if (customFill)
        return customFill;
    if (variant === 'secondary')
        return ui.secondary;
    if (variant === 'ghost')
        return ui.surface;
    if (variant === 'danger')
        return ui.danger;
    return ui.primary;
}
function refreshUiNode(node, ui) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    var type = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.componentType;
    if (!type || !UI_COMPONENT_TYPES.has(type))
        return;
    // Compound widgets: full chrome rebuild (installed at create time)
    var rebuild = (_b = node.metadata) === null || _b === void 0 ? void 0 : _b.uiRebuild;
    if (typeof rebuild === 'function') {
        rebuild();
        return;
    }
    var parts = getParts(node);
    var state = getState(node);
    if (type === 'button') {
        var variant = String((_c = state.variant) !== null && _c !== void 0 ? _c : 'primary');
        var disabled = Boolean(state.disabled);
        var customFill = state.hasCustomFill ? String((_d = state.fill) !== null && _d !== void 0 ? _d : '') : '';
        var nextFill = buttonFill(ui, variant, customFill, disabled);
        if (!state.hasCustomFill) {
            node.metadata.componentState = __assign(__assign({}, state), { fill: buttonFill(ui, variant, '', false) });
        }
        setFill(parts.bg, nextFill);
        if (parts.text) {
            var themedText = variant === 'ghost' ? ui.textSecondary : ui.textInverse;
            var textColor = state.hasCustomColor
                ? String((_f = (_e = state.textColor) !== null && _e !== void 0 ? _e : state.color) !== null && _f !== void 0 ? _f : themedText)
                : themedText;
            setFill(parts.text, disabled ? ui.textMuted : textColor);
            var bw = Number(state.width);
            if (Number.isFinite(bw) && bw > 0) {
                parts.text.x = bw / 2;
            }
            if (state.hasCustomFontSize) {
                var fs = Number(state.fontSize);
                if (Number.isFinite(fs))
                    setFontSize(parts.text, fs);
            }
            else {
                setFontSize(parts.text, fontSizeForControl(ui, String((_g = state.size) !== null && _g !== void 0 ? _g : 'md')));
            }
        }
        if (variant === 'ghost')
            setStroke(parts.bg, ui.border);
        return;
    }
    if (type === 'label') {
        if (!state.hasCustomColor) {
            setFill(node, ui.textMuted);
        }
        if (!state.hasCustomFontSize) {
            setFontSize(node, ui.fontSizeSm);
        }
        return;
    }
    if (type === 'toggle') {
        var on = Boolean((_h = state.value) !== null && _h !== void 0 ? _h : state.checked);
        var disabled = Boolean(state.disabled);
        setFill(parts.track, disabled ? ui.border : on ? ui.primary : ui.borderStrong);
        setFill(parts.knob, ui.surface);
        return;
    }
    if (type === 'checkbox') {
        var checked = Boolean((_j = state.checked) !== null && _j !== void 0 ? _j : state.value);
        var disabled = Boolean(state.disabled);
        setFill(parts.box, disabled ? ui.surfaceMuted : checked ? ui.primary : ui.surface);
        setStroke(parts.box, disabled ? ui.border : checked ? ui.primary : ui.borderStrong);
        return;
    }
    if (type === 'slider') {
        setFill(parts.track, ui.surfaceInset);
        setFill(parts.fill, ui.primary);
        setFill(parts.thumb, ui.surface);
        setStroke(parts.thumb, ui.primary);
        return;
    }
    if (type === 'progressBar') {
        var variant = String((_k = state.variant) !== null && _k !== void 0 ? _k : 'default');
        var fill = variant === 'success'
            ? ui.success
            : variant === 'warning'
                ? ui.warning
                : variant === 'danger'
                    ? ui.danger
                    : ui.primary;
        setFill(parts.track, ui.surfaceInset);
        setFill(parts.fillBar, fill);
        return;
    }
    if (type === 'card') {
        if (parts.bg) {
            setFill(parts.bg, ui.surface);
            setStroke(parts.bg, ui.border);
        }
        if (parts.header) {
            setFill(parts.header, ui.surfaceMuted);
            setStroke(parts.header, ui.border);
        }
        if (parts.title)
            setFill(parts.title, ui.textMuted);
        if (parts.subtitle)
            setFill(parts.subtitle, ui.textSecondary);
        return;
    }
    if (type === 'dialog') {
        if (parts.overlay)
            setFill(parts.overlay, ui.overlay);
        if (parts.panel) {
            setFill(parts.panel, ui.surface);
            setStroke(parts.panel, ui.border);
        }
        if (parts.titleText)
            setFill(parts.titleText, ui.text);
        return;
    }
    if (type === 'menu') {
        if (parts.bg) {
            setFill(parts.bg, ui.surface);
            setStroke(parts.bg, ui.border);
        }
        else if ('children' in node && node.children[0]) {
            setFill(node.children[0], ui.surface);
            setStroke(node.children[0], ui.border);
        }
        // Item labels (text nodes after bg)
        if ('children' in node) {
            var variants = (_l = state.itemVariants) !== null && _l !== void 0 ? _l : [];
            var items = (_m = state.items) !== null && _m !== void 0 ? _m : [];
            var children = node.children;
            for (var i = 1; i < children.length; i++) {
                var item = (_o = items[i - 1]) !== null && _o !== void 0 ? _o : '';
                var danger = variants[i - 1] === 'danger' ||
                    ['delete', 'remove', 'danger'].includes(String(item).toLowerCase());
                setFill(children[i], danger ? ui.danger : ui.text);
            }
        }
        return;
    }
    if (type === 'input' || type === 'textarea') {
        var disabled = Boolean(state.disabled);
        var invalid = Boolean(state.invalid);
        setFill(parts.bg, disabled ? ui.surfaceMuted : ui.surface);
        setStroke(parts.bg, invalid ? ui.danger : ui.border);
        if (parts.text) {
            var hasValue = Boolean(state.value);
            var themed = hasValue ? ui.text : ui.textPlaceholder;
            var textColor = hasValue && state.hasCustomColor
                ? String((_q = (_p = state.textColor) !== null && _p !== void 0 ? _p : state.color) !== null && _q !== void 0 ? _q : themed)
                : themed;
            setFill(parts.text, textColor);
            if (state.hasCustomFontSize) {
                var fs = Number(state.fontSize);
                if (Number.isFinite(fs))
                    setFontSize(parts.text, fs);
            }
            else {
                setFontSize(parts.text, ui.fontSize);
            }
        }
        return;
    }
    if (type === 'radio') {
        var selected = Boolean((_r = state.selected) !== null && _r !== void 0 ? _r : state.checked);
        setStroke(parts.outer, selected ? ui.primary : ui.borderStrong);
        setFill(parts.inner, selected ? ui.primary : 'transparent');
        return;
    }
    if (type === 'tooltip') {
        if (parts.anchor)
            setFill(parts.anchor, ui.primary);
        if (parts.bg)
            setFill(parts.bg, ui.surfaceInset);
        if (parts.label)
            setFill(parts.label, ui.textInverse);
        return;
    }
    if (type === 'toast') {
        var variant = String((_s = state.variant) !== null && _s !== void 0 ? _s : 'success');
        var fills = {
            success: ui.surfaceInset,
            error: ui.surfaceInset,
            warning: ui.surfaceInset,
            info: ui.primaryMuted,
        };
        if (parts.bg)
            setFill(parts.bg, (_t = fills[variant]) !== null && _t !== void 0 ? _t : fills.success);
        if (parts.text)
            setFill(parts.text, ui.textInverse);
        return;
    }
    if (type === 'statusBar') {
        if (parts.bg) {
            setFill(parts.bg, ui.surfaceInset);
            setStroke(parts.bg, ui.border);
        }
        if (parts.primarySeg)
            setFill(parts.primarySeg, ui.primaryMuted);
        var primaryIndex = numState(state, 'primaryIndex', 0);
        var segments = (_u = state.segments) !== null && _u !== void 0 ? _u : [];
        for (var i = 0; i < segments.length; i++) {
            setFill(parts["seg".concat(i)], i === primaryIndex ? ui.text : ui.textMuted);
        }
    }
}
function numState(state, key, fallback) {
    var v = state[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
/** Walk stage and update canvas UI component part colors (per-node uiTheme wins). */
export function refreshCanvasUi(root, app) {
    var visit = function (n) {
        var e_1, _a;
        var state = getState(n);
        var tokens = app ? resolveEffectiveUiTokens(app, state) : {};
        var ui = resolveUiCanvasTheme(tokens);
        runWithCanvasUiTheme(ui, function () { return refreshUiNode(n, ui); });
        if ('children' in n) {
            try {
                for (var _b = __values(n.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var child = _c.value;
                    visit(child);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
    };
    visit(root);
}
